import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Zap, ArrowRight, Check, Clock, Globe, Shield, Wrench, Users, Award } from 'lucide-react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';

const AboutPage = () => {
  useCanonical('https://ecuflashservice.com/about');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About ECU Flash Service",
    "description": "Professional ECU tuning service with years of experience across all major vehicle brands and ECU platforms.",
    "url": "https://ecuflashservice.com/about",
    "mainEntity": {
      "@type": "AutoRepair",
      "name": "ECU Flash Service",
      "description": "Professional ECU tuning and remapping service",
      "url": "https://ecuflashservice.com",
      "email": "support@ecuflashservice.com",
      "foundingDate": "2020",
      "areaServed": "Worldwide",
      "serviceType": ["ECU Tuning", "ECU Remapping", "DPF Delete", "EGR Delete", "AdBlue Delete"]
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ecuflashservice.com" },
      { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://ecuflashservice.com/about" }
    ]
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>About Us | ECU Flash Service | Professional ECU Tuning Team</title>
        <meta name="description" content="Meet the team behind ECU Flash Service. Years of hands-on experience with Bosch, Siemens, Denso, and Continental ECU platforms. Trusted by customers in 50+ countries." />
        <meta name="keywords" content="about ECU Flash Service, ECU tuning team, professional ECU tuners, who we are, ECU tuning experience" />
        <meta property="og:title" content="About ECU Flash Service" />
        <meta property="og:description" content="Professional ECU tuning service with years of hands-on calibration experience." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://ecuflashservice.com/favicon-512.png" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <span className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</span>
            </Link>
            <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
              <span>Get Started</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#00F0FF]/30 mb-6">
            <Users className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00F0FF]">About Us</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
            <span className="text-white">Who</span>{' '}
            <span className="text-[#00F0FF]">We Are</span>
          </h1>
          <p className="text-[#888] text-lg max-w-2xl mx-auto">
            We're a small team of ECU calibration engineers who got tired of seeing bad tunes ruin good engines. So we built a service that does it properly.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-[#0A0A0A] border border-white/10 p-8 mb-12">
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
              Our Story
            </h2>
            <div className="space-y-4 text-[#888] leading-relaxed">
              <p>
                ECU Flash Service started because we kept seeing the same problem: customers bringing us ECU files that had been "tuned" by someone who clearly didn't understand the calibration. Smoke limiter maps zeroed out. Boost targets set dangerously high. Checksums not corrected. Engines running rough or throwing codes. People were paying for tunes and getting garbage in return.
              </p>
              <p>
                We'd spent years learning how ECU calibrations actually work - not from YouTube tutorials, but from reverse-engineering map structures, understanding fuel system dynamics, and building up a library of safe, tested calibrations across every major ECU platform. We figured if we were going to fix other people's mistakes anyway, we might as well offer the service properly from the start.
              </p>
              <p>
                That was a few years ago. Since then, we've calibrated thousands of ECU files across Bosch, Siemens, Denso, Continental, Delphi, and Marelli platforms. Cars, trucks, vans, agricultural equipment, marine engines - if it has an ECU, we've probably seen it. Every file that leaves our hands has been individually calibrated, checksum-corrected, and quality-checked against our internal database of known-good tunes.
              </p>
              <p>
                We're not a faceless corporation. We're a small team that takes this seriously because our reputation depends on every single file being right.
              </p>
            </div>
          </div>

          {/* What Sets Us Apart */}
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#00F0FF] mb-8 pb-2 border-b border-white/10">
            What Sets Us Apart
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <Wrench className="w-8 h-8 text-[#00F0FF] mb-3" />
              <h3 className="font-heading text-lg font-bold uppercase text-white mb-2">
                Hands-On Experience
              </h3>
              <p className="text-[#888] text-sm leading-relaxed">
                We don't use automated tuning tools that apply generic maps. Every file is opened, analysed, and modified by a human who understands the specific ECU architecture, map structure, and safe operating limits for your engine. There's no substitute for experience, and ours is measured in thousands of files.
              </p>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <Shield className="w-8 h-8 text-[#00F0FF] mb-3" />
              <h3 className="font-heading text-lg font-bold uppercase text-white mb-2">
                Conservative by Default
              </h3>
              <p className="text-[#888] text-sm leading-relaxed">
                We tune for reliability, not for bragging rights on a forum. Our calibrations stay within the mechanical safety margins of your engine. We'd rather give you 95% of the possible gains with 100% reliability than push for that last 5% and risk something breaking at 80,000 miles.
              </p>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <Clock className="w-8 h-8 text-[#00F0FF] mb-3" />
              <h3 className="font-heading text-lg font-bold uppercase text-white mb-2">
                Fast Turnaround
              </h3>
              <p className="text-[#888] text-sm leading-relaxed">
                Most files are returned within 20-60 minutes. We know your vehicle might be on a lift at a workshop, or you might have a customer waiting. We work as fast as we can without rushing the calibration. Complex multi-system jobs might take longer, but we'll always tell you upfront.
              </p>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <Globe className="w-8 h-8 text-[#00F0FF] mb-3" />
              <h3 className="font-heading text-lg font-bold uppercase text-white mb-2">
                Worldwide Service
              </h3>
              <p className="text-[#888] text-sm leading-relaxed">
                We've sent modified files to customers in over 50 countries across every continent. Time zones don't stop us - we operate extended hours to cover customers from Australia to the Americas. Your location doesn't matter; the quality of work is the same.
              </p>
            </div>
          </div>

          {/* Our Expertise */}
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#00F0FF] mb-8 pb-2 border-b border-white/10">
            What We Work With
          </h2>

          <div className="bg-[#0A0A0A] border border-white/10 p-8 mb-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-heading text-lg font-bold uppercase text-white mb-4">ECU Platforms</h3>
                <ul className="space-y-2">
                  {[
                    "Bosch EDC15, EDC16, EDC17, MD1, MG1",
                    "Bosch MED9, MED17, MEVD17",
                    "Siemens/Continental SID, PCR, EMS",
                    "Denso (Toyota, Lexus, Subaru)",
                    "Delphi DCM3, DCM6",
                    "Marelli (Fiat, Alfa Romeo)",
                    "Kefico (Hyundai, Kia)"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-[#888] text-sm">
                      <Check className="w-4 h-4 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold uppercase text-white mb-4">Vehicle Types</h3>
                <ul className="space-y-2">
                  {[
                    "Passenger cars (all European, Asian, American brands)",
                    "SUVs and 4x4s (Land Cruiser, Hilux, Ranger, Navara)",
                    "Light commercial vans (Transit, Sprinter, Crafter)",
                    "Heavy trucks (DAF, MAN, Scania, Volvo, Mercedes Actros)",
                    "Agricultural equipment (tractors, harvesters)",
                    "Marine engines (inboard and outboard)",
                    "Motorcycles and powersports"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-[#888] text-sm">
                      <Check className="w-4 h-4 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Numbers */}
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#00F0FF] mb-8 pb-2 border-b border-white/10">
            By the Numbers
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              { number: "5,000+", label: "Files Calibrated" },
              { number: "50+", label: "Countries Served" },
              { number: "20-60", label: "Min Avg Turnaround" },
              { number: "4.9/5", label: "Customer Rating" }
            ].map((stat, i) => (
              <div key={i} className="bg-[#0A0A0A] border border-white/10 p-6 text-center">
                <div className="font-heading text-2xl md:text-3xl font-bold text-[#00F0FF] mb-1">
                  {stat.number}
                </div>
                <div className="text-xs text-[#888] uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Our Guarantee */}
          <div className="bg-[#0A0A0A] border border-[#00F0FF]/30 p-8 mb-12">
            <div className="flex items-start gap-4">
              <Award className="w-8 h-8 text-[#00F0FF] flex-shrink-0" />
              <div>
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
                  Our Guarantee
                </h2>
                <div className="space-y-3 text-[#888] leading-relaxed">
                  <p>
                    Every file we send comes with a straightforward guarantee: if it's not right, we fix it for free. If we can't make it right, you get your money back. No arguments, no hoops to jump through.
                  </p>
                  <p>
                    We also include free revisions for 30 days after delivery. If a fault code pops up that we missed, or you want the calibration adjusted (a bit more aggressive, a bit more conservative), just send the file back and we'll revise it at no extra charge.
                  </p>
                  <p>
                    We stand behind our work because we're confident in it. Every file is calibrated against our internal database of successful tunes and quality-checked before it leaves our hands. If something slips through, that's on us, and we'll make it right.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* How to Work With Us */}
          <div className="bg-[#0A0A0A] border border-white/10 p-8 mb-12">
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
              How to Work With Us
            </h2>
            <div className="space-y-4 text-[#888] leading-relaxed">
              <p>
                <strong className="text-white">Individual customers:</strong> Upload your ECU file through our website, select the services you want, and pay securely via PayPal. We'll have your modified file back in your inbox within 20-60 minutes. It's that simple.
              </p>
              <p>
                <strong className="text-white">Workshops and tuning businesses:</strong> If you're sending us files regularly, get in touch about trade pricing. We work with workshops in dozens of countries and can set up a workflow that suits your business.
              </p>
              <p>
                <strong className="text-white">Fleet operators:</strong> Economy tuning across a fleet can save significant fuel costs. We offer volume pricing and can work through your preferred workshop. The ROI usually pays back within 3-4 months.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Placement */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-[#888] mb-6">
            Upload your ECU file and tell us what you need. We'll take care of the rest.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-white transition inline-flex items-center justify-center gap-2">
              <span>Start Your Order</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-skew border border-white/30 text-white font-bold uppercase tracking-wider px-6 py-3 hover:border-[#00F0FF] transition">
              <span>Contact Us</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
